import React, { useEffect, useState, useRef } from 'react';
import '../styles/GoogleTranslate.css';

const LANGUAGES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
];

let scriptLoaded = false;
let scriptLoading = false;
const initializedIds = new Set();

const loadGoogleScript = () => {
  if (scriptLoaded || scriptLoading) return;
  scriptLoading = true;
  const script = document.createElement('script');
  script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  script.async = true;
  document.head.appendChild(script);
};

const suppressBanner = () => {
  document.querySelectorAll('.goog-te-banner-frame, .skiptranslate iframe').forEach(el => {
    el.style.setProperty('display', 'none', 'important');
    el.style.setProperty('height', '0', 'important');
  });
  if (document.body.style.top && document.body.style.top !== '0px') {
    document.body.style.setProperty('top', '0px', 'important');
  }
};

const injectBannerStyle = () => {
  if (document.getElementById('gt-banner-suppress')) return;
  const style = document.createElement('style');
  style.id = 'gt-banner-suppress';
  style.textContent = `
    .goog-te-banner-frame, .goog-te-banner-frame.skiptranslate, .skiptranslate iframe {
      display: none !important; height: 0 !important;
    }
    body { top: 0 !important; margin-top: 0 !important; }
    /* Hide the actual Google widget — we use our own UI */
    .gt-hidden-widget { position: absolute !important; opacity: 0 !important;
      pointer-events: none !important; width: 1px !important; height: 1px !important;
      overflow: hidden !important; left: -9999px !important; }
  `;
  document.head.appendChild(style);
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

export const applyLanguage = (langCode) => {
  const select = document.querySelector('select.goog-te-combo');
  
  if (langCode === 'en') {
    // Clear cookies for all possible domains/paths
    const domains = [window.location.hostname, `.${window.location.hostname}`, ''];
    const paths = ['/', '/app', ''];
    
    domains.forEach(domain => {
      paths.forEach(path => {
        const base = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
        document.cookie = `${base} path=${path};${domain ? ` domain=${domain};` : ''}`;
      });
    });

    if (select) {
      select.value = '';
      select.dispatchEvent(new Event('change'));
    }
    
    // For English, a reload is the only 100% reliable way to restore the original 
    // text nodes that Google mutated.
    window.location.reload();
    return;
  }

  const cookieVal = `/en/${langCode}`;
  document.cookie = `googtrans=${cookieVal}; path=/;`;
  document.cookie = `googtrans=${cookieVal}; domain=${window.location.hostname}; path=/;`;

  if (select) {
    select.value = langCode;
    select.dispatchEvent(new Event('change'));
  } else {
    // Fallback: reload if widget isn't initialized yet
    window.location.reload();
  }
};

const GoogleTranslate = ({ id = 'google_translate_element' }) => {
  const [open, setOpen] = useState(false);
  const [activeLang, setActiveLang] = useState(() => {
    const cookie = getCookie('googtrans');
    if (cookie) {
      const parts = cookie.split('/');
      return parts[parts.length - 1] || 'en';
    }
    return 'en';
  });
  const wrapperRef = useRef(null);

  useEffect(() => {
    injectBannerStyle();

    // Expose init callback
    window.googleTranslateElementInit = () => {
      scriptLoaded = true;
      scriptLoading = false;
      window.dispatchEvent(new Event('google-translate-ready'));
    };

    const initWidget = () => {
      if (initializedIds.has(id)) return;
      const container = document.getElementById(id);
      if (!container || !window.google?.translate) return;
      try {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,hi,mr',
            // HORIZONTAL creates a <select> we can control programmatically
            layout: window.google.translate.TranslateElement.InlineLayout.HORIZONTAL,
            autoDisplay: false,
          },
          id
        );
        initializedIds.add(id);
      } catch (e) {
        console.warn('GT init error:', e);
      }
    };

    window.addEventListener('google-translate-ready', initWidget);
    if (window.google?.translate) {
      initWidget();
    } else {
      loadGoogleScript();
    }

    const poll = setInterval(() => {
      if (!initializedIds.has(id)) initWidget();
      else clearInterval(poll);
    }, 800);

    const observer = new MutationObserver(suppressBanner);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    suppressBanner();

    return () => {
      clearInterval(poll);
      observer.disconnect();
      window.removeEventListener('google-translate-ready', initWidget);
    };
  }, [id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (code) => {
    setActiveLang(code);
    setOpen(false);
    applyLanguage(code);
  };

  return (
    <div className="gt-wrapper" ref={wrapperRef}>
      {/* Hidden Google widget (provides the select element for API access) */}
      <div id={id} className="gt-hidden-widget" aria-hidden="true" />

      {/* Our custom button */}
      <button
        className="gt-button"
        onClick={() => setOpen(o => !o)}
        title="Change language"
        aria-label="Change language"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="gt-icon" aria-hidden="true">
          <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v2h11.17A15.52 15.52 0 0 1 9 11.35 10.9 10.9 0 0 1 6.69 8h-2a13 13 0 0 0 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7 1.62-4.33L19.12 17h-3.24z"/>
        </svg>
      </button>

      {/* Custom dropdown */}
      {open && (
        <div className="gt-dropdown" role="listbox" aria-label="Select language">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              role="option"
              aria-selected={activeLang === lang.code}
              className={`gt-option ${activeLang === lang.code ? 'gt-option--active' : ''}`}
              onClick={() => handleSelect(lang.code)}
            >
              <span className="gt-option-native">{lang.native}</span>
              {activeLang === lang.code && (
                <svg viewBox="0 0 24 24" fill="currentColor" className="gt-check" aria-hidden="true">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GoogleTranslate;
